import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useEvent, useNotifications } from '../../context';
import { getAccount, getValue, type Account, type Purse } from '../../api/event';
import { ActivityIndicator, Icon, InstallButton } from '../../components/ui';
import { ListItem } from '../../components/lists';
import { BalanceInfo } from '../../components';
import { images } from '../../config/colors';
import tocBackgroundFallback from '../../assets/tocBackground.jpg';
import styles from './AccountPage.module.css';

const tocBackground = images.background || tocBackgroundFallback;

export function AccountPage() {
  const navigate = useNavigate();
  const { loginKey } = useAuth();
  const { selectedEvent } = useEvent();
  const { refreshNotifications } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState<Account | null>(null);
  const [purse, setPurse] = useState<Purse | null>(null);

  const loadAccount = useCallback(async () => {
    if (!selectedEvent || !loginKey) return;

    setLoading(true);
    const response = await getAccount(loginKey, selectedEvent.id);
    setLoading(false);

    if (!response.ok) {
      alert("Couldn't retrieve account information. Please wait a minute and try again.");
      return;
    }

    if (response.data?.error === null && response.data?.account) {
      setAccount(response.data.account);
    }
  }, [selectedEvent, loginKey]);

  const loadValue = useCallback(async () => {
    if (!account?.cid || !loginKey) return;

    setLoading(true);
    const response = await getValue(loginKey, account.cid);
    setLoading(false);

    if (!response.ok) {
      alert("Couldn't retrieve balance information. Please wait a minute and try again.");
      return;
    }

    if (response.data?.purses?.length && response.data.purses.length > 0) {
      const mainPurse = response.data.purses.find((p) => p.purse_num === 1);
      if (mainPurse) {
        setPurse(mainPurse);
      }
    }
  }, [account, loginKey]);

  useEffect(() => {
    loadAccount();
    refreshNotifications();
  }, [loadAccount]);

  useEffect(() => {
    loadValue();
  }, [loadValue]);

  const handleRefresh = () => {
    setAccount(null);
    setPurse(null);
    loadAccount();
  };

  if (!selectedEvent) {
    return (
      <div className={styles.noEvent}>
        <p>Please select an event from the Event Info tab.</p>
        <button onClick={() => navigate('/event')} className={styles.eventButton}>
          Go to Event Info
        </button>
      </div>
    );
  }

  return (
    <div
      className={styles.background}
      style={{ backgroundImage: `url(${tocBackground})` }}
    >
      <ActivityIndicator visible={loading} />
      <div className={styles.container}>
        <div className={styles.eventBox}>
          <div className={styles.eventInfo}>
            <h2 className={styles.eventText}>{selectedEvent.title_text}</h2>
            <p className={styles.dateText}>{selectedEvent.date_text}</p>
          </div>
          <button className={styles.reloadButton} onClick={handleRefresh}>
            <Icon name="reload" backgroundColor="var(--toc-blue)" iconColor="white" size={40} />
          </button>
        </div>

        <BalanceInfo value={purse?.value} size={64} />

        <div className={styles.menu}>
          <ListItem
            title="Buy Coins"
            IconComponent={<Icon name="plus" backgroundColor="var(--toc-blue)" iconColor="white" />}
            onPress={() => navigate('/buy-coins', { state: { account, event: selectedEvent } })}
          />
          <ListItem
            title="Pay Vendor"
            IconComponent={<Icon name="cart" backgroundColor="var(--toc-blue)" iconColor="white" />}
            onPress={() => navigate('/pay-vendor', { state: { account } })}
          />
          <ListItem
            title="Share Coins"
            IconComponent={<Icon name="gift" backgroundColor="var(--toc-blue)" iconColor="white" />}
            onPress={() => navigate('/share-coins', { state: { account, event: selectedEvent, maxCoins: Number(purse?.value || 0) } })}
          />
          <ListItem
            title="Tip Vendor"
            IconComponent={<Icon name="tip" backgroundColor="var(--toc-blue)" iconColor="white" />}
            onPress={() => navigate('/tip-vendor', { state: { account, event: selectedEvent, maxCoins: Number(purse?.value || 0) } })}
          />
          <ListItem
            title="People's Choice Voting"
            IconComponent={<Icon name="trophy" backgroundColor="var(--toc-blue)" iconColor="white" />}
            onPress={() => navigate('/vote', { state: { event: selectedEvent } })}
          />
          <ListItem
            title="Payment Methods"
            IconComponent={<Icon name="credit-card" backgroundColor="var(--toc-blue)" iconColor="white" />}
            onPress={() => navigate('/payment-methods')}
          />
          <ListItem
            title="History"
            IconComponent={<Icon name="history" backgroundColor="var(--toc-blue)" iconColor="white" />}
            onPress={() => navigate('/history', { state: { cid: account?.cid } })}
          />
          <InstallButton />
        </div>
      </div>
    </div>
  );
}

export default AccountPage;
